// ===========================================
// Notification Preferences Form (Shared)
// ===========================================
// Multi-channel notification preferences form used by both
// parent and teacher notification preference pages.
// Supports Email, SMS, and WhatsApp channels with graceful
// degradation when Twilio services are unavailable.

import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Grid,
  TextField,
  Skeleton,
  Chip,
  Checkbox,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Schedule as ScheduleIcon,
  RestartAlt as RestartIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import PageHeader, { BreadcrumbItem } from '../common/PageHeader';
import {
  useExtendedNotificationPreferences,
  useNotificationServiceStatus,
  useUpdateExtendedPreferences,
  useUpdatePhone,
  useUpdateTypeSettings,
  useResetNotificationPreferences,
  useSendTestSms,
  useSendTestWhatsApp,
} from '../../hooks/useNotifications';
import {
  NotificationType,
  NotificationChannel,
} from '../../api/notifications.api';
import { useUserProfile } from '../settings/account/account.hooks';

// ===========================================
// TYPES
// ===========================================

export interface NotificationPreferencesFormProps {
  notificationTypeOrder: NotificationType[];
  notificationTypeLabels: Partial<Record<NotificationType, string>>;
  notificationTypeDescriptions: Partial<Record<NotificationType, string>>;
  breadcrumbs: BreadcrumbItem[];
}

// ===========================================
// COMPONENT
// ===========================================

export default function NotificationPreferencesForm({
  notificationTypeOrder,
  notificationTypeLabels,
  notificationTypeDescriptions,
  breadcrumbs,
}: NotificationPreferencesFormProps) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Queries
  const {
    data: extendedData,
    isLoading,
    error,
  } = useExtendedNotificationPreferences();
  const { data: serviceStatus } = useNotificationServiceStatus();
  const { data: userProfile } = useUserProfile();

  // Mutations
  const updateExtendedPrefs = useUpdateExtendedPreferences();
  const updatePhone = useUpdatePhone();
  const updateTypeSettings = useUpdateTypeSettings();
  const resetPreferences = useResetNotificationPreferences();
  const sendTestSms = useSendTestSms();
  const sendTestWhatsApp = useSendTestWhatsApp();

  const preferences = extendedData?.preferences;
  const typeSettings = extendedData?.typeSettings;

  // Phone comes from user profile, fall back to notification preferences
  const userPhone = userProfile?.phone || preferences?.phoneNumber || null;
  const hasPhone = !!userPhone;
  // Phone is synced to notification prefs and verified
  const phoneVerified = preferences?.phoneVerified ?? false;
  const phoneSynced = !!preferences?.phoneNumber;
  const phoneReady = hasPhone && phoneSynced && phoneVerified;

  const smsAvailable = serviceStatus?.smsEnabled ?? false;
  const whatsappAvailable = serviceStatus?.whatsappEnabled ?? false;
  const multiChannelAvailable = smsAvailable || whatsappAvailable;

  // Determine channel states
  const isEmailEnabled = preferences?.emailEnabled ?? true;
  const isSmsEnabled = preferences?.smsEnabled ?? false;
  const isWhatsappEnabled = preferences?.whatsappEnabled ?? false;
  const anyChannelEnabled = isEmailEnabled || isSmsEnabled || isWhatsappEnabled;

  const isUpdating =
    updateExtendedPrefs.isPending ||
    updatePhone.isPending ||
    updateTypeSettings.isPending ||
    resetPreferences.isPending;

  // ===========================================
  // HANDLERS
  // ===========================================

  // Strip spaces/dashes from phone, keep leading +
  const normalizePhone = (phone: string): string =>
    phone.startsWith('+')
      ? '+' + phone.slice(1).replace(/\D/g, '')
      : phone.replace(/\D/g, '');

  const handleGlobalChannelToggle = (channel: 'emailEnabled' | 'smsEnabled' | 'whatsappEnabled') => {
    if (!preferences) return;
    const enabling = !preferences[channel];

    // When enabling SMS or WhatsApp, sync the profile phone first if needed
    if (enabling && (channel === 'smsEnabled' || channel === 'whatsappEnabled') && userPhone && !phoneSynced) {
      updatePhone.mutate(
        { phoneNumber: normalizePhone(userPhone) },
        {
          onSuccess: () => {
            updateExtendedPrefs.mutate({ [channel]: true });
          },
        }
      );
      return;
    }

    updateExtendedPrefs.mutate({ [channel]: enabling });
  };

  const handleTypeChannelToggle = (
    type: NotificationType,
    channel: NotificationChannel,
    currentChannels: NotificationChannel[]
  ) => {
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter((c) => c !== channel)
      : [...currentChannels, channel];
    updateTypeSettings.mutate({
      notificationType: type,
      channels: newChannels,
    });
  };

  const handleQuietHoursToggle = () => {
    if (!preferences) return;
    updateExtendedPrefs.mutate({
      quietHoursEnabled: !preferences.quietHoursEnabled,
    });
  };

  const handleQuietHoursChange = (
    field: 'quietHoursStart' | 'quietHoursEnd',
    value: string
  ) => {
    if (!preferences) return;
    updateExtendedPrefs.mutate({ [field]: value });
  };

  const handleReset = () => {
    resetPreferences.mutate();
    setShowResetConfirm(false);
  };

  const handleSendTestSms = () => {
    sendTestSms.mutate({ message: 'Test SMS from Music \'n Me' });
  };

  const handleSendTestWhatsApp = () => {
    sendTestWhatsApp.mutate({ message: 'Test WhatsApp from Music \'n Me' });
  };

  // ===========================================
  // LOADING / ERROR
  // ===========================================

  if (isLoading) {
    return (
      <Box>
        <PageHeader
          title="Notification Preferences"
          subtitle="Manage your notification settings"
          breadcrumbs={breadcrumbs}
        />
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={300} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader
          title="Notification Preferences"
          subtitle="Manage your notification settings"
          breadcrumbs={breadcrumbs}
        />
        <Alert severity="error">
          Failed to load notification preferences. Please try again.
        </Alert>
      </Box>
    );
  }

  // ===========================================
  // HELPERS
  // ===========================================

  const getTypeChannels = (type: NotificationType): NotificationChannel[] => {
    if (typeSettings && typeSettings[type]) {
      const val = typeSettings[type];
      if (Array.isArray(val)) return val;
    }
    // Fallback: use legacy notificationTypes field
    const typeValue = preferences?.notificationTypes?.[type];
    if (Array.isArray(typeValue)) return typeValue as NotificationChannel[];
    // Boolean fallback
    if (typeValue === false) return [];
    if (typeValue === true) return ['EMAIL'];
    return ['EMAIL'];
  };

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <Box>
      <PageHeader
        title="Notification Preferences"
        subtitle="Manage your notification settings"
        breadcrumbs={breadcrumbs}
      />

      {/* Card 1: Global Channel Toggles */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Notification Channels
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose which channels you want to receive notifications on
          </Typography>

          <Stack spacing={2}>
            {/* Email Toggle (always shown) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                px: 2,
                borderRadius: 1,
                bgcolor: 'background.default',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.light',
                    borderRadius: '50%',
                    p: 1,
                    color: 'primary.main',
                    display: 'flex',
                  }}
                >
                  <EmailIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle1">Email</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isEmailEnabled
                      ? 'Receive notifications via email'
                      : 'Email notifications are disabled'}
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={isEmailEnabled}
                onChange={() => handleGlobalChannelToggle('emailEnabled')}
                disabled={isUpdating}
                color="primary"
              />
            </Box>

            {/* SMS Toggle (shown only if service available) */}
            {smsAvailable && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 2,
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: '#e0f2f1',
                      borderRadius: '50%',
                      p: 1,
                      color: '#00897b',
                      display: 'flex',
                    }}
                  >
                    <SmsIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">SMS</Typography>
                      {hasPhone && (
                        <Chip
                          label={phoneVerified ? 'Verified' : 'Not Verified'}
                          color={phoneVerified ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {!hasPhone
                        ? 'Add a phone number in Account Settings to enable SMS'
                        : !phoneVerified
                          ? 'Verify your phone number in Account Settings to enable SMS'
                          : isSmsEnabled
                            ? 'Receive notifications via text message'
                            : 'SMS notifications are disabled'}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={isSmsEnabled}
                  onChange={() => handleGlobalChannelToggle('smsEnabled')}
                  disabled={isUpdating || !phoneReady}
                  color="success"
                />
              </Box>
            )}

            {/* WhatsApp Toggle (shown only if service available) */}
            {whatsappAvailable && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 2,
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor: '#e8f5e9',
                      borderRadius: '50%',
                      p: 1,
                      color: '#25D366',
                      display: 'flex',
                    }}
                  >
                    <WhatsAppIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">WhatsApp</Typography>
                      {hasPhone && (
                        <Chip
                          label={phoneVerified ? 'Verified' : 'Not Verified'}
                          color={phoneVerified ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      {hasPhone && phoneVerified && preferences?.whatsappOptedIn && (
                        <Chip
                          label="Opted In"
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {!hasPhone
                        ? 'Add a phone number in Account Settings to enable WhatsApp'
                        : !phoneVerified
                          ? 'Verify your phone number in Account Settings to enable WhatsApp'
                          : !preferences?.whatsappOptedIn
                            ? 'Opt in via Account Settings to enable WhatsApp notifications'
                            : isWhatsappEnabled
                              ? 'Receive notifications via WhatsApp'
                              : 'WhatsApp notifications are disabled'}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={isWhatsappEnabled}
                  onChange={() => handleGlobalChannelToggle('whatsappEnabled')}
                  disabled={
                    isUpdating ||
                    !phoneReady ||
                    !preferences?.whatsappOptedIn
                  }
                  color="success"
                />
              </Box>
            )}

            {/* Test Messages (inline) */}
            {phoneReady && (isSmsEnabled || isWhatsappEnabled) && (
              <Box
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Send a test message to verify your setup
                </Typography>
                <Stack direction="row" spacing={2}>
                  {isSmsEnabled && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        sendTestSms.isPending ? (
                          <CircularProgress size={14} />
                        ) : (
                          <SendIcon />
                        )
                      }
                      onClick={handleSendTestSms}
                      disabled={isUpdating || sendTestSms.isPending}
                    >
                      Send Test SMS
                    </Button>
                  )}
                  {isWhatsappEnabled && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        sendTestWhatsApp.isPending ? (
                          <CircularProgress size={14} />
                        ) : (
                          <SendIcon />
                        )
                      }
                      onClick={handleSendTestWhatsApp}
                      disabled={isUpdating || sendTestWhatsApp.isPending}
                      sx={{ color: '#25D366', borderColor: '#25D366', '&:hover': { borderColor: '#1da851', bgcolor: '#e8f5e9' } }}
                    >
                      Send Test WhatsApp
                    </Button>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Notification Type Channel Matrix */}
      <Card sx={{ mb: 3, opacity: anyChannelEnabled ? 1 : 0.5 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box
              sx={{
                bgcolor: 'info.light',
                borderRadius: '50%',
                p: 1.5,
                display: 'flex',
                color: 'info.main',
              }}
            >
              <NotificationsIcon />
            </Box>
            <Box>
              <Typography variant="h6">Notification Types</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose which types of notifications you want to receive
                {multiChannelAvailable && ' and on which channels'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Column headers for multi-channel */}
          {multiChannelAvailable && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                mb: 1,
              }}
            >
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" spacing={2} sx={{ minWidth: 200, justifyContent: 'flex-end' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 56, textAlign: 'center' }}
                >
                  Email
                </Typography>
                {smsAvailable && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: 56, textAlign: 'center' }}
                  >
                    SMS
                  </Typography>
                )}
                {whatsappAvailable && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: 56, textAlign: 'center' }}
                  >
                    WhatsApp
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          <Stack spacing={1}>
            {notificationTypeOrder.map((type) => {
              const channels = getTypeChannels(type);
              const label = notificationTypeLabels[type] || type;
              const description = notificationTypeDescriptions[type] || '';

              // Email-only fallback (no multi-channel)
              if (!multiChannelAvailable) {
                const isEnabled = channels.includes('EMAIL');
                return (
                  <Box
                    key={type}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1">{label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {description}
                      </Typography>
                    </Box>
                    <Switch
                      checked={isEnabled}
                      onChange={() =>
                        handleTypeChannelToggle(
                          type,
                          'EMAIL',
                          channels
                        )
                      }
                      disabled={isUpdating || !isEmailEnabled}
                      size="small"
                    />
                  </Box>
                );
              }

              // Multi-channel: checkbox matrix
              return (
                <Box
                  key={type}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    bgcolor: 'background.default',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">{label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {description}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ minWidth: 200, justifyContent: 'flex-end' }}
                  >
                    {/* Email checkbox */}
                    <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                      <Checkbox
                        size="small"
                        checked={channels.includes('EMAIL')}
                        onChange={() =>
                          handleTypeChannelToggle(type, 'EMAIL', channels)
                        }
                        disabled={isUpdating || !isEmailEnabled}
                      />
                    </Box>

                    {/* SMS checkbox */}
                    {smsAvailable && (
                      <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                          size="small"
                          checked={channels.includes('SMS')}
                          onChange={() =>
                            handleTypeChannelToggle(type, 'SMS', channels)
                          }
                          disabled={isUpdating || !isSmsEnabled}
                        />
                      </Box>
                    )}

                    {/* WhatsApp checkbox */}
                    {whatsappAvailable && (
                      <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                          size="small"
                          checked={channels.includes('WHATSAPP')}
                          onChange={() =>
                            handleTypeChannelToggle(type, 'WHATSAPP', channels)
                          }
                          disabled={isUpdating || !isWhatsappEnabled}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Card 4: Quiet Hours */}
      <Card sx={{ mb: 3, opacity: anyChannelEnabled ? 1 : 0.5 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'secondary.light',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  color: 'secondary.main',
                }}
              >
                <ScheduleIcon />
              </Box>
              <Box>
                <Typography variant="h6">Quiet Hours</Typography>
                <Typography variant="body2" color="text.secondary">
                  {smsAvailable || whatsappAvailable
                    ? `Hold back ${[smsAvailable && 'SMS', whatsappAvailable && 'WhatsApp']
                        .filter(Boolean)
                        .join(' and ')} during these hours. Email always arrives straight away.`
                    : 'Applies to SMS and WhatsApp only — email always arrives straight away.'}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={preferences?.quietHoursEnabled ?? true}
              onChange={handleQuietHoursToggle}
              disabled={isUpdating || !anyChannelEnabled}
              color="secondary"
            />
          </Box>

          {preferences?.quietHoursEnabled && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={preferences?.quietHoursStart || '21:00'}
                    onChange={(e) =>
                      handleQuietHoursChange('quietHoursStart', e.target.value)
                    }
                    disabled={isUpdating || !anyChannelEnabled}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End Time"
                    type="time"
                    value={preferences?.quietHoursEnd || '07:00'}
                    onChange={(e) =>
                      handleQuietHoursChange('quietHoursEnd', e.target.value)
                    }
                    disabled={isUpdating || !anyChannelEnabled}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Grid>
              </Grid>
              {/* ⚠️ This copy previously promised "notifications will be queued
                  during quiet hours and sent when the period ends". No such
                  deferral has ever existed on any channel — messages landing in
                  the window are DROPPED, not held (notificationDispatcher
                  .service.ts, SKIPPED_QUIET_HOURS). It also named email first,
                  which stopped being gated at all on 2026-08-30. Say what the
                  system does, not what we once intended it to do. */}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Messages that would arrive during these hours are skipped, not
                delivered later. Email is never affected — it always arrives
                immediately and waits in your inbox.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Card 5: Reset to Defaults */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: 'warning.light',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  color: 'warning.main',
                }}
              >
                <RestartIcon />
              </Box>
              <Box>
                <Typography variant="h6">Reset to Defaults</Typography>
                <Typography variant="body2" color="text.secondary">
                  Restore all notification settings to their default values
                </Typography>
              </Box>
            </Box>
            {!showResetConfirm ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setShowResetConfirm(true)}
                disabled={isUpdating}
              >
                Reset
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={() => setShowResetConfirm(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleReset}
                  disabled={isUpdating}
                  startIcon={
                    isUpdating ? <CircularProgress size={16} /> : undefined
                  }
                >
                  Confirm Reset
                </Button>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Update status */}
      {isUpdating && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
}
